import { type ApiProblem, type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import type { DepositMethod, ReservationStatus } from '@rental/domain'
import { format, formatDateTime, formatEuro } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, type RefObject, useEffect, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useAccess } from '../access-context'
import { api } from '../api'
import type { CanDo } from '../booking-can'
import { DrawerField } from '../cash/DrawerField'
import { refreshDrawersAfter } from '../cash/queries'
import { ChoiceChips } from '../components/ChoiceChips'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { mayTakeFocus, OutcomeNotice } from '../components/OutcomeNotice'
import { PendingLine } from '../components/PendingBadge'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { TechnicalLines } from '../components/TechnicalDetails'
import { ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { centsForInput, parseEuroCents } from '../money'
import { declineKind } from './decline'

type Deposit = ApiSchemas['StaffDeposit']
type DepositRecord = NonNullable<Deposit['current']>
type DepositDone = ApiSchemas['DepositDone']

/** While the reader waits for the card, the screen asks again this often (the webhook records it). */
const WAITING_REFRESH_MS = 3_000

type Done = 'taken' | 'released' | 'kept'

/** What the counter asked to keep, held while the question is open (D11). */
type KeepRequest = {
  method: DepositMethod
  amountCents: number
  /** What goes back: released on the card, or handed back in cash. */
  restCents: number
  reason: string
  /** Only when cash goes back: the drawer it leaves. */
  cashSessionId: string | null
}

type RunOptions = {
  /** Right after a success, before the screen refreshes: a question closes while it is still there. */
  onSuccess?: () => void
  /**
   * The reader's steps (a card asked for, stopped, checked, simulated): their buttons go
   * with the state they belong to, so the deposit as it then stands takes the focus.
   */
  follow?: boolean
}

/** The record an action on the reader changed, and the state it changed from (none: new). */
type Follow = { id: string; from: DepositRecord['state'] | null }

type Request = () => Promise<{ ok: true; data: DepositDone } | { ok: false; problem: ApiProblem }>

/**
 * PAY-2 and PAY-3 at the counter: take the category's deposit (on the reader,
 * or recorded by hand from the shop's POS or in cash), see when a card hold
 * lapses, let it go, or keep part of it with a reason. The server decides the
 * amount and every rule; this only asks.
 */
export function DepositPanel({
  code,
  deposit,
  status,
  balanceCents,
  endsAt,
  can,
}: {
  code: string
  deposit: Deposit
  status: ReservationStatus
  balanceCents: number
  endsAt: string
  /**
   * S9: the booking's own answer. A trip takes a deposit by hand on its
   * delivery and lets it go on its collection; the card reader at the counter
   * and keeping part of a deposit stay with the office.
   */
  can: CanDo
}) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [done, setDone] = useState<Done | null>(null)
  // S5: a cash deposit is given back from an open drawer.
  const [cashSessionId, setCashSessionId] = useState<string | null>(null)
  // D11: keeping part of the deposit is asked once more. The question is the panel's, not
  // the form's: a refusal can end the hold (a lapsed card) and take the form away, and the
  // answer must still show, with the key a retry replays.
  const keepDialog = useRef<HTMLDialogElement>(null)
  const [keeping, setKeeping] = useState<KeepRequest | null>(null)
  const [keepOpen, setKeepOpen] = useState(false)
  // A refusal said in the question stays in sight once it closes, and is not said again.
  const [problemSaid, setProblemSaid] = useState(false)
  const [follow, setFollow] = useState<Follow | null>(null)
  const panel = useRef<HTMLDivElement>(null)
  const keepSent = useRef<{ body: string; key: string } | null>(null)
  const releaseWhyId = useId()
  const current = deposit.current
  const cashHeld = current?.state === 'held' && current.method === 'cash'
  // Cash goes back from an open drawer: until one is chosen, the release says so.
  const drawerMissing = cashHeld && cashSessionId === null && busy !== 'release'
  const live = current?.state === 'pending' || current?.state === 'held'
  const amount = deposit.amountCents

  const refresh = async () => {
    await Promise.all(
      [['booking', code], ['checkout', code], ['today']].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    )
  }

  // The card is on the reader: Stripe's webhook records the hold, the screen catches up.
  useEffect(() => {
    if (current?.state !== 'pending') return
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['booking', code] })
      void queryClient.invalidateQueries({ queryKey: ['checkout', code] })
    }, WAITING_REFRESH_MS)
    return () => clearInterval(timer)
  }, [current?.state, code, queryClient])

  async function run(
    action: string,
    request: Request,
    outcome: Done | null,
    options: RunOptions = {},
  ): Promise<boolean> {
    const before = current
    setProblem(null)
    setProblemSaid(false)
    setDone(null)
    setFollow(null)
    setBusy(action)
    const result = await request()
    setBusy(null)
    if (result.ok) {
      options.onSuccess?.()
      if (outcome) setDone(outcome)
      if (options.follow) {
        const after = result.data.deposit.current
        if (after) setFollow({ id: after.id, from: after.id === before?.id ? before.state : null })
      }
      await refresh()
      return true
    }
    // A refusal may still have changed something (an online payment found, a lapsed hold).
    await refresh()
    await refreshDrawersAfter(queryClient, result.problem)
    if (!takeOver(result.problem)) setProblem(result.problem)
    return false
  }

  const check = () =>
    run(
      'check',
      () =>
        call(api.POST('/v1/staff/bookings/{code}/deposit/check', { params: { path: { code } } })),
      null,
      { follow: true },
    )
  const simulate = () =>
    run(
      'simulate',
      () =>
        call(
          api.POST('/v1/staff/bookings/{code}/deposit/simulate', { params: { path: { code } } }),
        ),
      null,
      { follow: true },
    )
  const release = () =>
    run(
      'release',
      () =>
        call(
          api.POST('/v1/staff/bookings/{code}/deposit/release', {
            params: { path: { code }, header: { 'idempotency-key': newIdempotencyKey() } },
            body: cashHeld && cashSessionId ? { cashSessionId } : {},
          }),
        ),
      current?.state === 'held' ? 'released' : null,
      // Stopped while the card was awaited: the deposit as it ends takes the focus.
      { follow: current?.state === 'pending' },
    )

  function askKeep(request: KeepRequest) {
    // Drawn before it opens, so the question has its name and words when the focus lands in it.
    flushSync(() => {
      setProblem(null)
      setDone(null)
      setKeeping(request)
      setKeepOpen(true)
    })
    keepDialog.current?.showModal()
  }

  async function keep() {
    if (!keeping) return
    const body = {
      amountCents: keeping.amountCents,
      reason: keeping.reason,
      ...(keeping.cashSessionId ? { cashSessionId: keeping.cashSessionId } : {}),
    }
    // A retry of the same request replays; a different one gets its own key.
    const serialized = JSON.stringify(body)
    if (keepSent.current?.body !== serialized) {
      keepSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    const key = keepSent.current.key
    const ok = await run(
      'keep',
      () =>
        call(
          api.POST('/v1/staff/bookings/{code}/deposit/capture', {
            params: { path: { code }, header: { 'idempotency-key': key } },
            body,
          }),
        ),
      'kept',
      { onSuccess: () => keepDialog.current?.close() },
    )
    if (ok) keepSent.current = null
  }

  const withReader = can('cardReader')
  const releases = can('depositRelease')
  const keeps = can('depositKeep')
  const mayTake =
    can('depositTake') &&
    !live &&
    amount !== null &&
    amount > 0 &&
    ['confirmed', 'checked_out'].includes(status)

  return (
    <div ref={panel} className="flex flex-col gap-4">
      <p>
        {amount === null
          ? m.deposit.unset
          : amount === 0
            ? m.deposit.none
            : format(m.deposit.amount, { amount: formatEuro(amount, locale) })}
      </p>
      {deposit.required && status === 'confirmed' && current?.state !== 'held' ? (
        <Notice tone="info">
          <p>{m.deposit.required}</p>
        </Notice>
      ) : null}

      {current ? (
        <RecordFocus
          // Another deposit is another record: read from the start when it comes.
          key={current.id}
          takeFocus={follow?.id === current.id && follow.from !== current.state}
          region={panel}
          onDone={() => setFollow(null)}
        >
          <DepositState record={current} endsAt={endsAt} />
        </RecordFocus>
      ) : null}

      {/* Waiting on the card reader: checking it, its test card and giving up are the reader's. */}
      {current?.state === 'pending' && withReader ? (
        <div className="flex flex-col gap-3">
          {deposit.simulated ? (
            <p className="text-sm text-text-muted">{m.deposit.simulated}</p>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {deposit.simulated ? (
              <Button
                variant="staff"
                busy={busy === 'simulate'}
                disabled={busy !== null && busy !== 'simulate'}
                onClick={() => void simulate()}
              >
                {m.deposit.simulate}
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="rounded-sm"
              busy={busy === 'check'}
              disabled={busy !== null && busy !== 'check'}
              onClick={() => void check()}
            >
              {m.deposit.check}
            </Button>
            <Button
              variant="outline"
              className="rounded-sm"
              busy={busy === 'release'}
              disabled={busy !== null && busy !== 'release'}
              onClick={() => void release()}
            >
              {m.deposit.stopWaiting}
            </Button>
          </div>
        </div>
      ) : null}

      {current?.state === 'held' ? (
        <div className="flex flex-col gap-4">
          {releases ? (
            <div className="flex flex-col gap-3">
              {cashHeld ? (
                <DrawerField
                  name={`deposit-release-${code}`}
                  code={code}
                  onChange={setCashSessionId}
                />
              ) : null}
              <div>
                <Button
                  variant="outline"
                  className="rounded-sm"
                  busy={busy === 'release'}
                  disabled={(busy !== null && busy !== 'release') || drawerMissing}
                  aria-describedby={drawerMissing ? releaseWhyId : undefined}
                  onClick={() => void release()}
                >
                  {current.method === 'cash' ? m.deposit.releaseCash : m.deposit.release}
                </Button>
              </div>
              <ReasonList id={releaseWhyId} reasons={drawerMissing ? [m.deposit.why.drawer] : []} />
            </div>
          ) : null}
          {['checked_out', 'returned'].includes(status) && keeps ? (
            balanceCents > 0 ? (
              <KeepForm
                code={code}
                method={current.method}
                depositCents={current.amountCents}
                maxCents={Math.min(balanceCents, current.amountCents)}
                busy={busy === 'keep'}
                disabled={busy !== null && busy !== 'keep'}
                onAsk={askKeep}
              />
            ) : status === 'returned' ? (
              <p className="text-sm text-text-muted">{m.deposit.keepNothing}</p>
            ) : null
          ) : null}
        </div>
      ) : null}

      {mayTake && amount !== null ? (
        <TakeForm
          code={code}
          amountCents={amount}
          reader={withReader ? deposit.reader : null}
          cashAccepted={deposit.cashAccepted}
          busy={busy === 'take'}
          disabled={busy !== null && busy !== 'take'}
          run={run}
        />
      ) : null}

      {done ? (
        <OutcomeNotice key={done}>
          <p>{m.deposit.done[done]}</p>
        </OutcomeNotice>
      ) : null}
      {/* While the question is open its answer shows in it; closed, here. */}
      {keepOpen ? null : <ProblemNotice problem={problem} quiet={problemSaid} />}
      {keeps ? (
        <ConfirmDialog
          dialogRef={keepDialog}
          // Asked nothing, it holds nothing: no hidden copy of a question already answered.
          title={
            keeping
              ? format(m.deposit.keepConfirm.title, {
                  amount: formatEuro(keeping.amountCents, locale),
                })
              : null
          }
          confirmLabel={
            keeping
              ? format(m.deposit.keepConfirm.confirm, {
                  amount: formatEuro(keeping.amountCents, locale),
                })
              : ''
          }
          cancelLabel={m.lifecycle.back}
          busy={busy === 'keep'}
          onConfirm={() => void keep()}
          onClose={() => {
            setProblemSaid(problem !== null)
            setKeepOpen(false)
            setKeeping(null)
          }}
        >
          {keeping ? <KeepSummary request={keeping} /> : null}
          {keepOpen ? <ProblemNotice problem={problem} /> : null}
        </ConfirmDialog>
      ) : null}
    </div>
  )
}

/** D11: what keeping does, in words: the payment, what happens to the rest, the reason. */
function KeepSummary({ request }: { request: KeepRequest }) {
  const { locale, messages: m } = useLocale()
  const asked = m.deposit.keepConfirm
  const euro = (cents: number) => formatEuro(cents, locale)
  return (
    <div className="flex flex-col gap-2">
      <p>{format(asked.payment, { amount: euro(request.amountCents) })}</p>
      {request.restCents > 0 ? (
        <p>
          {format(request.method === 'cash' ? asked.cash : asked.released, {
            amount: euro(request.restCents),
          })}
        </p>
      ) : null}
      <p className="whitespace-pre-line">
        {format(m.lifecycle.reasonLine, { reason: request.reason })}
      </p>
      <p className="font-medium">{asked.final}</p>
    </div>
  )
}

type Run = (
  action: string,
  request: Request,
  outcome: Done | null,
  options?: RunOptions,
) => Promise<boolean>

/**
 * The deposit as it stands, where the reader's progress shows. A card asked
 * for on the reader, or stopped, checked or simulated there: the buttons
 * pressed go with the state they belonged to, and the deposit as it then
 * stands takes the focus. Only from nothing or from inside the panel: while a
 * card is awaited the sheet is read again every few seconds, and an answer that
 * comes late never takes the focus from where the counter has gone meanwhile.
 */
function RecordFocus({
  takeFocus,
  region,
  onDone,
  children,
}: {
  takeFocus: boolean
  region: RefObject<HTMLDivElement | null>
  onDone: () => void
  children: ReactNode
}) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!takeFocus) return
    if (box.current && mayTakeFocus(region.current, { fromButtons: false })) box.current.focus()
    onDone()
  }, [takeFocus, region, onDone])
  return (
    <div ref={box} tabIndex={-1} className="flex flex-col gap-4 rounded-panel focus:outline-none">
      {children}
    </div>
  )
}

/** What the deposit is now, in the counter's words. */
function DepositState({ record, endsAt }: { record: DepositRecord; endsAt: string }) {
  const { locale, messages: m } = useLocale()
  const amount = formatEuro(record.amountCents, locale)
  if (record.state === 'pending') {
    return (
      <Notice tone="info" live="polite">
        <p>{format(m.deposit.waiting, { amount })}</p>
      </Notice>
    )
  }
  if (record.state === 'held') {
    return (
      <div className="flex flex-col gap-2" data-testid="deposit-held">
        <p className="font-medium">
          {format(m.deposit.held[record.method], {
            amount,
            brand: (record.cardBrand ?? '').toUpperCase(),
          })}
        </p>
        {record.reference ? (
          <p className="text-sm text-text-muted">
            {format(m.deposit.posReference, { reference: record.reference })}
          </p>
        ) : null}
        {record.captureBefore ? (
          <p>
            {format(m.deposit.captureBefore, {
              date: formatDateTime(locale, record.captureBefore),
            })}
          </p>
        ) : null}
        {record.lapsesBeforeReturn ? (
          <Notice tone="warning">
            <p>{format(m.deposit.lapses, { date: formatDateTime(locale, endsAt) })}</p>
          </Notice>
        ) : null}
        {record.cardFunding === 'debit' || record.cardFunding === 'prepaid' ? (
          <p className="text-sm text-text-muted">{m.deposit.debit}</p>
        ) : null}
        <p className="text-sm text-text-muted">
          {format(m.deposit.takenBy, {
            name: record.takenBy,
            date: formatDateTime(locale, record.takenAt),
          })}
        </p>
      </div>
    )
  }
  const date = record.endedAt ? formatDateTime(locale, record.endedAt) : ''
  if (record.state === 'captured') {
    const kept = record.capturedCents ?? 0
    return (
      <p>
        {format(
          record.method === 'cash' ? m.deposit.ended.capturedCash : m.deposit.ended.captured,
          {
            kept: formatEuro(kept, locale),
            date,
            reason: record.captureReason ?? '',
            rest: formatEuro(record.amountCents - kept, locale),
          },
        )}
      </p>
    )
  }
  if (record.state === 'failed') {
    return (
      <>
        <Notice tone="warning">
          <p>
            {format(m.deposit.ended.failed, {
              why: m.deposit.declined[declineKind(record.failureCode)],
            })}
          </p>
        </Notice>
        <TechnicalLines
          lines={record.failureCode ? [format(m.app.stripeCode, { code: record.failureCode })] : []}
        />
      </>
    )
  }
  if (record.state === 'expired') {
    return (
      <Notice tone="danger">
        <p>{format(m.deposit.ended.expired, { date })}</p>
      </Notice>
    )
  }
  return <p>{format(m.deposit.ended.released, { date })}</p>
}

/** PAY-2: how the deposit is taken; the amount is the category's, never typed. */
function TakeForm({
  code,
  amountCents,
  reader,
  cashAccepted,
  busy,
  disabled,
  run,
}: {
  code: string
  amountCents: number
  /** Null where the card reader is not this person's to use (S9: a trip at the quay). */
  reader: Deposit['reader'] | null
  cashAccepted: boolean
  busy: boolean
  disabled: boolean
  run: Run
}) {
  const { locale, messages: m } = useLocale()
  const methods: DepositMethod[] = [
    ...(reader === 'ready' ? (['reader'] as const) : []),
    'pos',
    ...(cashAccepted ? (['cash'] as const) : []),
  ]
  const [method, setMethod] = useState<DepositMethod | ''>(reader === 'ready' ? 'reader' : '')
  const [reference, setReference] = useState('')
  // S5: a cash deposit goes into an open drawer.
  const [cashSessionId, setCashSessionId] = useState<string | null>(null)
  const lastSent = useRef<{ body: string; key: string } | null>(null)
  // UI-1c: what still stops the deposit, in words; none left means it can be taken.
  const why = m.deposit.why
  const reasons = [
    ...(method === '' ? [why.method] : []),
    ...(method === 'cash' && cashSessionId === null ? [why.drawer] : []),
  ]
  const ready = reasons.length === 0
  const reasonsId = useId()

  async function take() {
    if (method === '' || !ready) return
    const body = {
      method,
      ...(method === 'pos' && reference.trim() ? { reference: reference.trim() } : {}),
      ...(method === 'cash' && cashSessionId ? { cashSessionId } : {}),
    }
    // A retry of the same request replays; a different one gets its own key.
    const serialized = JSON.stringify(body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    const key = lastSent.current.key
    const ok = await run(
      'take',
      () =>
        call(
          api.POST('/v1/staff/bookings/{code}/deposit', {
            params: { path: { code }, header: { 'idempotency-key': key } },
            body,
          }),
        ),
      method === 'reader' ? null : 'taken',
      // On the reader the form goes while the card is awaited: the wait takes the focus.
      { follow: method === 'reader' },
    )
    if (ok) {
      lastSent.current = null
      setReference('')
    }
  }

  return (
    <SubPanel title={m.deposit.takeTitle}>
      {reader === 'pending' ? (
        // Cash is offered only where the category takes it (the sample settings, Q-63).
        <PendingLine>
          {cashAccepted ? m.deposit.readerPendingOrCash : m.deposit.readerPending}
        </PendingLine>
      ) : null}
      <ChoiceChips
        name={`deposit-method-${code}`}
        label={m.deposit.method}
        labelHidden
        options={methods.map((value) => ({ value, label: m.deposit.methods[value] }))}
        value={method}
        onChange={setMethod}
      />
      {method === 'pos' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={m.deposit.reference} optionalLabel={m.newBooking.optional}>
            {(control) => (
              <StaffInput
                {...control}
                maxLength={100}
                autoComplete="off"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            )}
          </Field>
        </div>
      ) : null}
      {method === 'cash' ? (
        <DrawerField name={`deposit-take-${code}`} code={code} onChange={setCashSessionId} />
      ) : null}
      <div className="flex flex-col gap-2">
        <div>
          <Button
            variant="staff"
            busy={busy}
            disabled={(disabled || !ready) && !busy}
            aria-describedby={ready || busy ? undefined : reasonsId}
            onClick={() => void take()}
          >
            {format(m.deposit.take, { amount: formatEuro(amountCents, locale) })}
          </Button>
        </div>
        {busy ? null : <ReasonList id={reasonsId} reasons={reasons} />}
      </div>
    </SubPanel>
  )
}

/**
 * PAY-3: keep part of the deposit, never more than what is owed, always with a
 * reason. The form gathers the answer and says what is missing; the panel asks
 * once more and sends it (D11).
 */
function KeepForm({
  code,
  method,
  depositCents,
  maxCents,
  busy,
  disabled,
  onAsk,
}: {
  code: string
  /** S5: a cash deposit gives the rest back from an open drawer. */
  method: DepositMethod
  depositCents: number
  maxCents: number
  busy: boolean
  disabled: boolean
  onAsk: (request: KeepRequest) => void
}) {
  const { locale, messages: m } = useLocale()
  const [amount, setAmount] = useState(() => centsForInput(maxCents, locale))
  const [reason, setReason] = useState('')
  const [cashSessionId, setCashSessionId] = useState<string | null>(null)
  const cents = parseEuroCents(amount)
  // All of it kept, nothing leaves the drawer: no drawer is asked for.
  const givesBack = method === 'cash' && cents !== null && cents < depositCents
  // UI-1c: what still stops it, in words; none left means it can be asked.
  const why = m.deposit.why
  const reasons = [
    ...(cents === null || cents <= 0 ? [why.amount] : []),
    ...(cents !== null && cents > maxCents
      ? [format(why.overMax, { amount: formatEuro(maxCents, locale) })]
      : []),
    ...(reason.trim().length < 3 ? [why.reason] : []),
    ...(givesBack && cashSessionId === null ? [why.drawerBack] : []),
  ]
  const ready = reasons.length === 0
  const reasonsId = useId()

  function ask() {
    if (!ready || cents === null) return
    onAsk({
      method,
      amountCents: cents,
      restCents: depositCents - cents,
      reason: reason.trim(),
      cashSessionId: givesBack ? cashSessionId : null,
    })
  }

  return (
    <SubPanel title={m.deposit.keepTitle}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={m.deposit.keepAmount}
          required
          hint={format(m.deposit.keepAmountHint, { amount: formatEuro(maxCents, locale) })}
          error={amount.trim() !== '' && cents === null ? m.payment.amountInvalid : null}
        >
          {(control) => (
            <StaffInput
              {...control}
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          )}
        </Field>
        <Field label={m.deposit.keepReason} hint={m.deposit.keepReasonHint} required>
          {(control) => (
            <StaffInput
              {...control}
              maxLength={500}
              autoComplete="off"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
      </div>
      {givesBack ? <DrawerField name={`deposit-keep-${code}`} onChange={setCashSessionId} /> : null}
      <div className="flex flex-col gap-2">
        <div>
          <Button
            variant="staff"
            busy={busy}
            disabled={(disabled || !ready) && !busy}
            aria-describedby={ready || busy ? undefined : reasonsId}
            onClick={ask}
          >
            {m.deposit.keep}
          </Button>
        </div>
        {busy ? null : <ReasonList id={reasonsId} reasons={reasons} />}
      </div>
    </SubPanel>
  )
}
